import { useState, useEffect, useRef, useCallback } from 'react'
import { useDualWallet } from './DualWalletContext'
import {
  getDlmmPoolInfo, openDlmmPosition, dlmmRange, getV2Balances, rawToUiString,
  checkV2Signature, SOL_RESERVE, DLMM_MAX_BINS, DLMM_DEFAULT_BINS,
} from '../utils/meteoraLp'

// ─── DlmmDepositForm: open a Meteora DLMM Spot position (lp-dlmm-20261015) ───
//
// Mounted by MeteoraLpPanel when the picked pool is DLMM (keyed by pool, so
// switching pools resets it). Amounts are free per side:
//   token only  -> bins ABOVE the active bin (sells as price rises)
//   pair only   -> bins BELOW the active bin (buys as price falls)
//   both        -> centred on the active bin, ±width
// Same send path as DAMM v2 (simulate -> wallet signs -> confirm w/ err check).

const WSOL = 'So11111111111111111111111111111111111111112'
const STATUS_LABEL = {
  checking: 'Checking balances…', building: 'Building transaction…', simulating: 'Simulating…',
  signing: 'Waiting for signature…', sending: 'Sending…', confirming: 'Confirming…', done: 'Confirmed',
}
const fmt = (n) => {
  if (n == null || !Number.isFinite(n)) return '—'
  if (n === 0) return '0'
  if (n < 0.0001) return n.toLocaleString('en-US', { maximumSignificantDigits: 3, useGrouping: false })
  if (n < 1) return n.toFixed(6).replace(/0+$/, '')
  if (n < 1000) return n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 })
}
const pos = (s) => s !== '' && Number.isFinite(Number(s)) && Number(s) > 0
const toRaw = (s, d) => {
  const [w, f = ''] = String(s).trim().split('.')
  return BigInt((w || '0') + (f + '0'.repeat(d)).slice(0, d))
}
const Solscan = ({ sig }) => <a href={`https://solscan.io/tx/${sig}`} target="_blank" rel="noreferrer">{sig.slice(0, 8)}…</a>
const errText = (e) => {
  const m = String(e?.message || e)
  return /reject|cancel|denied|declined/i.test(m) ? 'Cancelled in wallet — nothing was sent.' : m
}

export default function DlmmDepositForm({ pool, mint, symbol, balances, onBalancesStale, onOpened }) {
  const { publicKey, signTransaction, isReady } = useDualWallet()
  const owner = publicKey ? publicKey.toString() : null
  const [info, setInfo] = useState(null)
  const [infoErr, setInfoErr] = useState(null)
  const [tokenAmt, setTokenAmt] = useState('')
  const [pairAmt, setPairAmt] = useState('')
  const [bins, setBins] = useState(String(DLMM_DEFAULT_BINS))
  const [status, setStatus] = useState(null)
  const [sentSig, setSentSig] = useState(null)
  const [pending, setPending] = useState(null)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const aliveRef = useRef(true)
  const inFlightRef = useRef(false)
  useEffect(() => () => { aliveRef.current = false }, [])

  useEffect(() => {
    let alive = true
    setInfo(null); setInfoErr(null)
    getDlmmPoolInfo(pool.pool)
      .then(i => { if (alive) setInfo(i) })
      .catch(e => { if (alive) setInfoErr(String(e?.message || e)) })
    return () => { alive = false }
  }, [pool.pool])

  const tokenIsX = info ? info.mintX === mint : pool.isTokenA
  const pairMint = info ? (tokenIsX ? info.mintY : info.mintX) : pool.pairMint
  const pairSymbol = pool.pairSymbol || 'pair'
  const tokenDec = info ? (tokenIsX ? info.infoX.decimals : info.infoY.decimals) : null
  const pairDec = info ? (tokenIsX ? info.infoY.decimals : info.infoX.decimals) : null
  const tBal = balances?.[mint], pBal = balances?.[pairMint]
  const tMax = tBal && tokenDec != null ? rawToUiString(tBal.raw, tokenDec) : null
  const pMax = pBal && pairDec != null ? rawToUiString(pBal.raw, pairDec) : null

  const hasT = pos(tokenAmt), hasP = pos(pairAmt)
  const hasX = tokenIsX ? hasT : hasP, hasY = tokenIsX ? hasP : hasT
  const r = info && (hasT || hasP) ? dlmmRange(info.activeBinId, hasX, hasY, bins) : null
  // Prices in PAIR per TOKEN at the range ends.
  const binPrice = (id) => {
    if (!info) return null
    const pYX = info.priceYperX * Math.pow(1 + info.binStep / 10_000, id - info.activeBinId)
    return tokenIsX ? pYX : (pYX > 0 ? 1 / pYX : null)
  }
  const lo = r ? binPrice(tokenIsX ? r.minBinId : r.maxBinId) : null
  const hi = r ? binPrice(tokenIsX ? r.maxBinId : r.minBinId) : null
  const spot = info ? binPrice(info.activeBinId) : null

  const fundsProblem = (bal) => {
    if (!info || !bal) return null
    if (hasT && bal[mint] && toRaw(tokenAmt, tokenDec) > bal[mint].raw) return `Not enough ${symbol}.`
    if (hasP && bal[pairMint] && toRaw(pairAmt, pairDec) > bal[pairMint].raw) {
      return pairMint === WSOL ? `Not enough SOL (keeps ~${SOL_RESERVE} SOL back for rent/fees).` : `Not enough ${pairSymbol}.`
    }
    if (bal.lamports < BigInt(Math.round(SOL_RESERVE * 1e9))) return `Keep ~${SOL_RESERVE} SOL for position rent and fees.`
    return null
  }
  const fundsErr = fundsProblem(balances)

  const handleOpen = useCallback(async () => {
    if (!info || !isReady || !owner || inFlightRef.current || !(hasT || hasP)) return
    inFlightRef.current = true
    setError(null); setResult(null); setSentSig(null)
    const onStatus = (s, extra) => { if (!aliveRef.current) return; setStatus(s); if (extra?.signature) setSentSig(extra.signature) }
    try {
      setStatus('checking')
      const bal = await getV2Balances(owner, [mint, pairMint])
      if (!aliveRef.current) return
      const why = fundsProblem(bal)
      if (why) { setError(why); setStatus(null); return }
      const res = await openDlmmPosition({
        info, tokenIsX, tokenUi: hasT ? tokenAmt : '', pairUi: hasP ? pairAmt : '',
        bins, owner, signTransaction, onStatus,
      })
      if (!aliveRef.current) return
      setStatus(null)
      if (res.state === 'confirmed') { setResult(res); setTokenAmt(''); setPairAmt(''); onBalancesStale?.(); onOpened?.(res) }
      else if (res.state === 'pending') setPending(res)
      else setError('Not landed before its blockhash expired — it can no longer land. Safe to retry.')
    } catch (e) {
      if (aliveRef.current) { setError(errText(e)); if (e?.signature) setSentSig(e.signature); setStatus(null) }
    } finally {
      inFlightRef.current = false
    }
  }, [info, isReady, owner, hasT, hasP, tokenAmt, pairAmt, bins, mint, pairMint, tokenIsX, signTransaction, onBalancesStale, onOpened]) // eslint-disable-line react-hooks/exhaustive-deps

  const recheck = useCallback(async () => {
    if (!pending) return
    try {
      const s = await checkV2Signature(pending.signature)
      if (!aliveRef.current) return
      if (s === 'confirmed') { setResult(pending); setPending(null); onBalancesStale?.(); onOpened?.(pending) }
      else if (s === 'failed') { setSentSig(pending.signature); setPending(null); setError('Transaction failed on-chain — nothing was deposited.') }
    } catch {}
  }, [pending, onBalancesStale, onOpened])

  const busy = !!status && status !== 'done'
  const lock = busy || !!pending
  if (infoErr) return <div className="wallet-lp-note err">Couldn’t load DLMM pool: {infoErr}</div>
  if (!info) return <div className="wallet-lp-note">Loading DLMM pool…</div>

  return (
    <>
      <div className="wallet-lp-note">
        Spot · price {fmt(spot)} {pairSymbol}/{symbol} · bin step {info.binStep}
      </div>

      <label className="wallet-lp-label">
        Deposit {symbol}
        {tMax != null && <button className="wallet-lp-max" disabled={lock} onClick={() => setTokenAmt(tMax)}>max {fmt(Number(tMax))}</button>}
      </label>
      <input className="wallet-lp-input" inputMode="decimal" placeholder="0.0 (optional)" value={tokenAmt} disabled={lock}
        onChange={(e) => setTokenAmt(e.target.value.replace(/[^0-9.]/g, ''))} />

      <label className="wallet-lp-label">
        Deposit {pairSymbol}
        {pMax != null && <button className="wallet-lp-max" disabled={lock} onClick={() => setPairAmt(pMax)}>max {fmt(Number(pMax))}</button>}
      </label>
      <input className="wallet-lp-input" inputMode="decimal" placeholder="0.0 (optional)" value={pairAmt} disabled={lock}
        onChange={(e) => setPairAmt(e.target.value.replace(/[^0-9.]/g, ''))} />

      <label className="wallet-lp-label">
        Width (bins{hasT && hasP ? ' each side' : ''}, max {hasT && hasP ? Math.floor(DLMM_MAX_BINS / 2) : DLMM_MAX_BINS})
      </label>
      <input className="wallet-lp-input" inputMode="numeric" value={bins} disabled={lock}
        onChange={(e) => setBins(e.target.value.replace(/[^0-9]/g, ''))} />

      {r && (
        <div className="wallet-lp-quote">
          <div className="wallet-lp-quote-row">
            <span>Range</span>
            <strong>{fmt(lo)} – {fmt(hi)} {pairSymbol}</strong>
          </div>
          <div className="wallet-lp-quote-note">
            {hasT && hasP ? 'Two-sided, centred on the current price.'
              : hasT ? `${symbol} only — sits above the current price and sells into rises.`
              : `${pairSymbol} only — sits below the current price and buys dips.`}
            {' '}Earns only while price is inside the range. ~1% slippage on the active bin.
          </div>
        </div>
      )}

      {fundsErr && !error && <div className="wallet-lp-note err">{fundsErr}</div>}
      {!isReady && <div className="wallet-lp-note">Wallet locked — unlock or connect to continue.</div>}

      <button className="wallet-lp-go" disabled={!r || !isReady || lock || !!fundsErr} onClick={handleOpen}>
        {busy ? (STATUS_LABEL[status] || 'Working…') : pending ? 'Pending — check Solscan' : 'Add DLMM liquidity'}
      </button>

      {busy && sentSig && <div className="wallet-lp-note">Sent · <Solscan sig={sentSig} /></div>}
      {pending && (
        <div className="wallet-lp-note">
          Pending — check <Solscan sig={pending.signature} /> before retrying.{' '}
          <button className="wallet-lp-refresh" onClick={recheck}>re-check ↻</button>
        </div>
      )}
      {error && <div className="wallet-lp-note err">{error}{!busy && sentSig && <> · <Solscan sig={sentSig} /></>}</div>}
      {result && <div className="wallet-lp-note ok">DLMM position opened · <Solscan sig={result.signature} /></div>}
    </>
  )
}
